<template>
  <div :id="`${source}_call_campaigns`">
    <div
      ref="scrollContainerRef"
      class="flex flex-col gap-6 overflow-y-scroll"
      :class="{ 'px-[24vw] py-6': !isEdit }"
      style="max-height: calc(100vh - (128px + var(--banner-height)))"
    >
      <SkeletonLoader
        v-if="isLoading"
        :page="
          isEdit
            ? loaderPages.CALL_CAMPAIGN_EDIT
            : loaderPages.CALL_CAMPAIGN_CREATE
        "
      />

      <div
        v-else
        class="flex flex-col gap-6 proxima_nova secondary_color text-[13px]"
      >
        <DialerTypeSelector
          v-if="isParallelEnabled"
          :source="`${source}_call_campaigns`"
          :formData="createCampaignFormData"
        />

        <div id="call_via" class="form_col">
          <span>Call Via</span>
          <a-select
            v-if="isPowerDialer"
            v-model:value="createCampaignFormData.numberSelection"
            class="w-full"
            placeholder="Select number selection type"
            :options="numberSelectionOptions"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
              />
            </template>
          </a-select>

          <a-select
            v-else-if="isParallelDialer"
            v-model:value="createCampaignFormData.phoneNumberId"
            class="w-full"
            placeholder="Select a phone number"
            :options="displayPhoneNumbers"
            :field-names="{ label: 'phone_number', value: 'id' }"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
              />
            </template>

            <template #dropdownRender="{ menuNode: menu }">
              <v-nodes source="create_or_edit_call_campaign" :vnodes="menu" />
             <div class="select_vnode mt-1" @mousedown.prevent @click.stop="handleManagePhoneNumber">

                <ThemeImage
                  class="size-[14px]"
                  :icon="imageNames.SETTINGS_GREY"
                  alt="settings_icon"
                />
                <span class="text-sm">Manage Phone Numbers</span>
              </div>
            </template>
          </a-select>

          <div v-if="isPowerDialer" class="flex items-start gap-1">
            <ThemeImage
              :icon="imageNames.INFO_GREY"
              alt="info_icon"
              class="size-3 mt-0.5"
            />
            <span class="text-xs secondary_color">
              {{ createCampaignFormData.numberSelection === 'specific' 
                ? 'Calls will use only the numbers you select below.' 
                : 'Automatically call from a matching or nearby area code when available.' }}
            </span>
          </div>
        </div>

        <div v-if="isPowerDialer && createCampaignFormData.numberSelection === 'specific'" id="select_phone_numbers" class="form_col">
          <span>Select numbers to dial from</span>
          <a-select
            v-model:value="createCampaignFormData.phoneNumberId"
            class="w-full"
            placeholder="Select phone numbers"
            :options="displayPhoneNumbers"
            :field-names="{ label: 'phone_number', value: 'id' }"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
              />
            </template>

            <template #dropdownRender="{ menuNode: menu }">
              <v-nodes source="create_or_edit_call_campaign" :vnodes="menu" />

            <div class="select_vnode mt-1" @mousedown.prevent @click.stop="handleManagePhoneNumber">

                <ThemeImage
                  class="size-[14px]"
                  :icon="imageNames.SETTINGS_GREY"
                  alt="settings_icon"
                />
                <span class="text-sm">Manage Phone Numbers</span>
              </div>
            </template>
          </a-select>
        </div>

        <div id="call_voicemail" v-if="isAutoMachineDetectionEnabled" class="form_col">
          <span>Auto Voicemail Drop</span>
          <div class="form_col">
            <a-select
              v-model:value="createCampaignFormData.voicemailId"
              class="w-full"
              placeholder="Select a voicemail"
            >
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_white_arrow"
                  class="size-4"
                />
              </template>

              <template #dropdownRender="{ menuNode: menu }">
                <v-nodes source="create_or_edit_call_campaign" :vnodes="menu" />

               <div class="select_vnode mt-1" @mousedown.prevent @click.stop="handleManageVoicemails">
                  <ThemeImage
                    class="size-[14px]"
                    :icon="imageNames.SETTINGS_GREY"
                    alt="settings_icon"
                  />

                  <span class="text-sm">Manage Voicemails</span>
                </div>
              </template>

              <a-select-option :value="null" label="None">
                None
              </a-select-option>

              <a-select-option
                v-for="(voicemail, index) in voicemails"
                :key="`${voicemail.id}_number_${index}`"
                :value="voicemail.id"
                :label="voicemail.name"
              >
                {{ voicemail.name }}
              </a-select-option>
            </a-select>
          </div>
        </div>

        <CampaignFieldRenderer
          :field-type="campaignFieldTypes.COOLDOWN"
          v-model="createCampaignFormData.cooldownDuration"
        />

        <CampaignFieldRenderer
          v-if="!isEdit && !$route.params.campaignName && !$route.query.id"
          :field-type="campaignFieldTypes.AUDIENCE"
          :form-data="createCampaignFormData"
        />

        <CampaignFieldRenderer
          v-if="!isEdit"
          :field-type="campaignFieldTypes.RECORDING"
          v-model="createCampaignFormData.isRecordingEnabled"
        />

        <CampaignFieldRenderer
          :field-type="campaignFieldTypes.DNC_FOLLOW"
          v-model="createCampaignFormData.dncFollow"
        />

        <CampaignFieldRenderer
          :field-type="campaignFieldTypes.TIMEZONE_SETTINGS"
          :timezone-data="timezoneData"
          :scroll-container-ref="scrollContainerRef"
          @update:timezone-data="timezoneData = $event"
          @update:time-error="timeError = $event"
        />

        <CampaignFieldRenderer
          v-if="!isEdit"
          :field-type="campaignFieldTypes.ACTION_BUTTONS"
          :loading="creatingCallCampaign"
          :disabled="!isFormValid"
          @cancel="toCancelCreateCampaign"
          @create="toCreateCallCampaign"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  onMounted,
  ref,
  computed,
  watch,
  onUnmounted,
  defineComponent,
} from "vue";
import {
  toFetchVoicemails,
  toGetPhoneNumbers,
} from "@/src/utils/callsUtils.js";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { dialerTypes } from "@/src/constants/callConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { campaignFieldTypes, defaultActiveDays, numberSelectionOptions } from "@/src/constants/callCampaignConstants.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import CallService from "@/src/api/CallService.js";
import CampaignFieldRenderer from "@/src/components/campaigns/CampaignFieldRenderer.vue";
import DialerTypeSelector from "@/src/components/telephony_components/campaigns/DialerTypeSelector.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import BroadcastService from "@/src/services/communications/broadcastService.js";
import providerFeatureManager from "@/src/services/telephony/providers/providerFeatureManager.js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { imageNames } from "@/src/constants/imageConstants.js";

dayjs.extend(utc);

const props = defineProps({
  source: { type: String, required: true },
  isEdit: { type: Boolean, default: false },
  campaignName: { type: String, default: "Untitled Campaign" },
  campaignData: { type: Object, default: () => ({}) },
  entityListId: { type: Number, default: null },
});

const emit = defineEmits(["update:formData", "update:isValid"]);

const VNodes = defineComponent({
  props: {
    vnodes: {
      type: Object,
      required: true,
    },
  },

  render() {
    return this.vnodes;
  },
});

const router = useRouter();
const isLoading = ref(false);
const voicemails = ref(null);
const callViaList = ref(null);
const showCreateCampaignModal = ref(false);
const creatingCallCampaign = ref(false);
const timeError = ref("");
const scrollContainerRef = ref(null);
const originalPhoneNumberId = ref(null);

const sessionId = router.currentRoute.value.query.id;
const broadcastChannelName = sessionId ? `campaign-channel-${sessionId}` : 'campaign-channel';
const broadcastService = new BroadcastService(broadcastChannelName, true);

const storedEntityData = ref(null);

const createCampaignFormData = ref({
  name: props.campaignName,
  phoneNumberId: null,
  entityListId: props.entityListId,
  voicemailId: null,
  isRecordingEnabled: false,
  dialerType: dialerTypes.POWER_DIALER,
  parallelCalls: 2,
  cooldownDuration: 30,
  dncFollow: true,
  useLocalTimeZone: false,
  dayAllowed: defaultActiveDays,
  startTime: "9:00am",
  endTime: "5:00pm",
  numberSelection: "specific",
});

const timezoneData = computed({
  get: () => ({
    useLocalTimeZone: createCampaignFormData.value.useLocalTimeZone,
    dayAllowed: createCampaignFormData.value.dayAllowed,
    startTime: createCampaignFormData.value.startTime,
    endTime: createCampaignFormData.value.endTime,
  }),
  set: (value) => {
    createCampaignFormData.value.useLocalTimeZone = value.useLocalTimeZone;
    createCampaignFormData.value.dayAllowed = value.dayAllowed;
    createCampaignFormData.value.startTime = value.startTime;
    createCampaignFormData.value.endTime = value.endTime;
  },
});


const hasRouteWithCampaignName = computed(() => !!router.currentRoute.value.params.campaignName || !!router.currentRoute.value.query.id);

const isGlobalRecordingEnabled = computed(
  () => store.getters["dialerSettings/isGlobalRecordingEnabled"],
);

const isAutoMachineDetectionEnabled = computed(     
  () => store.getters["dialerSettings/isAutoMachineDetectionEnabled"],
);


const isParallelEnabled = computed(() => {
  return !!localStorage.getItem("parallel_dialer");
});

const isParallelDialer = computed(() => {
  return createCampaignFormData.value.dialerType === dialerTypes.PARALLEL_DIALER;
});

const isPowerDialer = computed(() => {
  return createCampaignFormData.value.dialerType === dialerTypes.POWER_DIALER;
});

const isFormValid = computed(() => {
  const hasEntityData = hasRouteWithCampaignName.value && storedEntityData.value;
  
  let phoneNumberValid = true;
  if (isPowerDialer.value && createCampaignFormData.value.numberSelection === 'specific') {
    phoneNumberValid = displayPhoneNumbers.value.some(
      (number) => number.id === createCampaignFormData.value.phoneNumberId
    );
  } else if (isPowerDialer.value && createCampaignFormData.value.numberSelection === 'auto') {
    phoneNumberValid = createCampaignFormData.value.phoneNumberId === 0;
  } else if (isParallelDialer.value) {
    phoneNumberValid = displayPhoneNumbers.value.some(
      (number) => number.id === createCampaignFormData.value.phoneNumberId
    );
  }
  
  const baseValidation = 
    createCampaignFormData.value.name && 
    phoneNumberValid && 
    !timeError.value;
  
  if (props.isEdit) {
    return baseValidation;
  }
  
  return baseValidation && (hasEntityData || createCampaignFormData.value.entityListId);
});

const handleStoreEntityData = (data) => {
  try {
    if (data && data.entityData) {
      storedEntityData.value = data.entityData;
      
      if (data.sourceCampaignName) {
      }
    } else {
      console.warn('Received data is missing entityData property:', data);
    }
  } catch (error) {
    console.error('Error handling store entity data:', error);
  }
};

const addStoredEntitiesToCampaign = async (campaignId) => {
  if (!storedEntityData.value) {
    return;
  }
  
  try {
    const addEntitiesPayload = {
      campaign_id: campaignId,
      ...storedEntityData.value
    };
    
    const addResp = await CallService.addEntitiesToCampaign(addEntitiesPayload);
    
    if (addResp?.success) {
      message.success(`Entities added to campaign successfully`);
    } else {
      const errorMessage = addResp?.error || 'Failed to add entities to campaign';
      message.error(errorMessage);
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('Error adding entities to campaign:', error);
    if (error.message && !error.message.includes('Failed to add entities')) {
      message.error('Failed to add entities to campaign');
    }
    throw error;
  }
};

const isProviderSupportedForParallelDialer = (provider) => {
  return providerFeatureManager.isParallelDialerSupported(provider);
};


const shouldAddCampaignNumber = (numbers) => {
  if (!props.isEdit || !props.campaignData?.phone_number) {
    return false;
  }

  const numberAlreadyExists = numbers.some(
    (number) => number.id === props.campaignData.phone_number_id
  );
  if (numberAlreadyExists) {
    return false;
  }

  if (!isParallelDialer.value) {
    return true;
  }

  return isProviderSupportedForParallelDialer(props.campaignData.provider);
};

const displayPhoneNumbers = computed(() => {
  let numbers = [...(callViaList.value || [])];

  if (isParallelDialer.value) {
    numbers = numbers.filter((number) => 
      isProviderSupportedForParallelDialer(number.provider)
    );
  }

  if (shouldAddCampaignNumber(numbers)) {
    numbers.push({
      id: props.campaignData.phone_number_id,
      phone_number: props.campaignData.phone_number,
      provider: props.campaignData.provider,
    });
  }

  return numbers;
});

const clearFormData = () => {
  createCampaignFormData.value = {
    name: props.campaignName,
    phoneNumberId: null,
    entityListId: null,
    voicemailId: null,
    isRecordingEnabled: false,
    dialerType: dialerTypes.POWER_DIALER,
    parallelCalls: 2,
    cooldownDuration: null,
    dncFollow: true,
    useLocalTimeZone: false,
    dayAllowed: defaultActiveDays,
    startTime: "9:00am",
    endTime: "5:00pm",
    numberSelection: "specific",
  };
  timeError.value = "";
};

const handleConsentCancel = () => {
  createCampaignFormData.value.isRecordingEnabled = false;
};

const handleManagePhoneNumber = () => {
  try {
    router.push("/settings#phone_numbers");
  } catch (error) {
    console.error(
      "An error occurred while handling manage phone number: ",
      error,
    );
  }
};

const handleManageVoicemails = () => {
  try {
    router.push("/settings#voice_mails");
  } catch (error) {
    console.error(
      "An error occurred while handling manage voicemails: ",
      error,
    );
  }
};

const handleDialerTypeChange = () => {
  if (createCampaignFormData.value.dialerType === dialerTypes.POWER_DIALER && 
      createCampaignFormData.value.numberSelection === 'auto') {
    createCampaignFormData.value.phoneNumberId = 0;
    return;
  }
  
  if (props.isEdit && originalPhoneNumberId.value) {
    const isOriginalNumberAvailable = displayPhoneNumbers.value.some(
      (number) => number.id === originalPhoneNumberId.value
    );
    
    if (isOriginalNumberAvailable) {
      createCampaignFormData.value.phoneNumberId = originalPhoneNumberId.value;
      return;
    }
  }
  
  const isCurrentNumberValid = displayPhoneNumbers.value.some(
    (number) => number.id === createCampaignFormData.value.phoneNumberId
  );
  
  if (!isCurrentNumberValid) {
    createCampaignFormData.value.phoneNumberId = displayPhoneNumbers.value[0]?.id || null;
  }
};

const handleNumberSelectionChange = (newSelection) => {
  if (newSelection === 'auto') {
    createCampaignFormData.value.phoneNumberId = 0;
  } else if (newSelection === 'specific') {
    if (!createCampaignFormData.value.phoneNumberId || createCampaignFormData.value.phoneNumberId === 0) {
      if (displayPhoneNumbers.value.length > 0) {
        createCampaignFormData.value.phoneNumberId = displayPhoneNumbers.value[0]?.id;
      }
    }
  }
};

const toCancelCreateCampaign = () => {
  try {
    router.push({ name: "sales-dialer-campaigns" });
  } catch (error) {
    console.error(
      "An error occurred while cancelling create campaign: ",
      error,
    );
  }
};

const handleGetData = async () => {
  try {
    isLoading.value = true;

    const [phoneNumbers, voicemailData] = await Promise.all([
      toGetPhoneNumbers({ user: true }),
      toFetchVoicemails(),
    ]);
    callViaList.value = phoneNumbers;
    voicemails.value = voicemailData;
  } catch (error) {
    console.error("An error occurred while handling get data: ", error);
  } finally {
    isLoading.value = false;
  }
};

const toCreateCallCampaign = async () => {
  if (timeError.value) return;

  try {
    creatingCallCampaign.value = true;
    const payload = {
      name: createCampaignFormData.value?.name,
      entity_list_id: createCampaignFormData.value?.entityListId,
      phone_number_id: createCampaignFormData.value?.phoneNumberId,
      voicemail_id: createCampaignFormData.value?.voicemailId,
      record_calls: createCampaignFormData.value?.isRecordingEnabled,
      cooldown_time: createCampaignFormData.value?.cooldownDuration,
      campaign_type: createCampaignFormData.value?.dialerType,
      parallel_calls: createCampaignFormData.value?.parallelCalls,
      dnc_follow: createCampaignFormData.value?.dncFollow,
    };

    if (isPowerDialer.value) {
      payload.number_selection = createCampaignFormData.value?.numberSelection;
    }

    if (createCampaignFormData.value?.useLocalTimeZone) {
      payload.day_allowed = createCampaignFormData.value?.dayAllowed;
      if (createCampaignFormData.value?.startTime) {
        payload.start_time = dayjs(createCampaignFormData.value.startTime, ["h:mma", "h:mm a"]).format("HH:mm");
      }
      if (createCampaignFormData.value?.endTime) {
        payload.end_time = dayjs(createCampaignFormData.value.endTime, ["h:mma", "h:mm a"]).format("HH:mm");
      }
    }

    const response = await CallService.createCallCampaign(payload);
    if (response?.success) {
      message.success("Call Campaign successfully created!");
      
      const isFromDialerPage = !!router.currentRoute.value.params.campaignName || !!router.currentRoute.value.query.id;

      let shouldRedirect = true;
      
      if (isFromDialerPage && storedEntityData.value) {
        try {
          await addStoredEntitiesToCampaign(response?.campaign?.id);
        } catch (error) {
          console.error('Failed to add entities to campaign:', error);
          shouldRedirect = false;
        }
      }
      
      if (shouldRedirect) {
      await router.push({
        name: "sales-dialer-campaign-detail",
        params: { id: response?.campaign?.id },
      });
      }
    }
  } catch (error) {
    message.error(
      error?.response?.data?.error ||
        `An error occurred while creating call campaign: ${error}`,
    );
  } finally {
    clearFormData();
    creatingCallCampaign.value = false;
    showCreateCampaignModal.value = false;
  }
};

watch(
  createCampaignFormData,
  (newVal) => {
    emit("update:formData", newVal);
  },
  { deep: true },
);

watch(
  () => props.campaignName,
  (newName) => {
    if (newName) {
      createCampaignFormData.value.name = newName;
    }
  },
  { immediate: true },
);

watch(callViaList, (newVal) => {
  if (newVal && !props.isEdit && (!createCampaignFormData.value.phoneNumberId || createCampaignFormData.value.phoneNumberId === 0)) {
    if (createCampaignFormData.value.numberSelection === 'specific' && callViaList.value?.length > 0) {
      createCampaignFormData.value.phoneNumberId = callViaList.value[0]?.id;
    }
  }
});

watch(showCreateCampaignModal, (newVal) => {
  if (newVal) {
    clearFormData();
  }
});

watch(voicemails, (newVal) => {
  if (newVal && newVal.length > 0 && !props.isEdit) {
    createCampaignFormData.value.voicemailId = null;
  }
});

watch(
  () => createCampaignFormData.value.isRecordingEnabled,
  (newVal) => {
    if (!store.getters["calls/recordingConsentStatus"] && newVal)
      eventBus.emit("open-consent-form", true);
  },
);

watch(
  () => createCampaignFormData.value.dialerType,
  (newDialerType) => {
    if (newDialerType) {
      handleDialerTypeChange();
      if (newDialerType === dialerTypes.POWER_DIALER && !props.isEdit) {
        createCampaignFormData.value.numberSelection = 'specific';
      }
    }
  }
);

watch(
  () => createCampaignFormData.value.numberSelection,
  handleNumberSelectionChange
);

watch(
  isFormValid,
  (newValue) => {
    if (props.isEdit) {
      emit("update:isValid", newValue);
    }
  },
  { immediate: true }
);

watch(
  [voicemails, () => props.campaignData],
  ([newVoicemails, newCampaignData]) => {
    if (props.isEdit && newVoicemails && newCampaignData) {
      const voicemailExists = newVoicemails.some(
        (voicemail) => voicemail.id === newCampaignData.voicemail_id,
      );

      createCampaignFormData.value.voicemailId = voicemailExists
        ? newCampaignData.voicemail_id
        : null;
    }
  },
  { immediate: true },
);

watch(
  [callViaList, () => props.campaignData],
  ([newCallViaList, newCampaignData]) => {
    if (props.isEdit && newCallViaList && newCampaignData && newCampaignData.phone_number_id) {
      originalPhoneNumberId.value = newCampaignData.phone_number_id;
      createCampaignFormData.value.phoneNumberId = newCampaignData.phone_number_id;
    }
  },
  { immediate: true },
);

watch(
  () => props.campaignData,
  (newVal) => {
    if (props.isEdit && newVal) {
      const useLocalTimeZone = !!(newVal.day_allowed && newVal.day_allowed.length > 0);
      
      createCampaignFormData.value = {
        ...createCampaignFormData.value,
        name: newVal.name,
        voicemailId: newVal.voicemail_id === 0 ? null : newVal.voicemail_id,
        dialerType: newVal.campaign_type,
        cooldownDuration: newVal.cooldown_time,
        parallelCalls: newVal.parallel_calls,
        isRecordingEnabled: newVal.record_calls,
        entityListId: newVal.entity_list_id,
        numberSelection: newVal.number_selection || 'specific',
        dncFollow: newVal.dnc_follow !== undefined ? newVal.dnc_follow : true,
        useLocalTimeZone: useLocalTimeZone,
        dayAllowed: newVal.day_allowed || defaultActiveDays,
        startTime: newVal.start_time ? dayjs(newVal.start_time, "HH:mm").format("h:mma") : "9:00am",
        endTime: newVal.end_time ? dayjs(newVal.end_time, "HH:mm").format("h:mma") : "5:00pm",
      };
    }
  },
  { immediate: true },
);

const sendChannelReady = () => {
  try {
    broadcastService.send('channel-ready', { 
      message: 'CreateOrEditCampaign is ready to receive data',
      sessionId: sessionId 
    });
  } catch (error) {
    console.error('Error sending channel-ready confirmation:', error);
  }
};

onMounted(async () => {
  await handleGetData();
  if (!props.isEdit) {
    createCampaignFormData.value.isRecordingEnabled =
      isGlobalRecordingEnabled.value;
  }
  eventBus.on("consent-cancel", handleConsentCancel);
  
  try {
    broadcastService.listen('store-entity-data', handleStoreEntityData);
    sendChannelReady();
  } catch (error) {
    console.error('Error setting up broadcast listener:', error);
  }
});

onUnmounted(() => {
  eventBus.off("consent-cancel", handleConsentCancel);
  broadcastService.off('store-entity-data', handleStoreEntityData);
  
  try {
  broadcastService.destroy();
  } catch (error) {
    console.error('Error destroying broadcast service:', error);
  }
});
</script>
